import { useState, useMemo } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';
import {
  useEntityTypes,
  useCreateEntityType,
  useUpdateEntityType,
  useDeleteEntityType,
  type EntityType,
  type CreateEntityTypePayload,
  type UpdateEntityTypePayload,
} from './hooks/useEntityTypes';

/**
 * EntityTypesPage — CRUD management page for user-defined entity types.
 * Allows tenants to configure entity types such as Individual, Corporation,
 * Trust, Partnership, Estate, Non-Profit, etc.
 *
 * Requirements: 9.4
 */
export function EntityTypesPage() {
  const { toast } = useToast();
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingType, setEditingType] = useState<EntityType | null>(null);

  const { data: entityTypes, isLoading } = useEntityTypes();
  const createMutation = useCreateEntityType();
  const updateMutation = useUpdateEntityType();
  const deleteMutation = useDeleteEntityType();

  const sortedTypes = useMemo(
    () => [...(entityTypes ?? [])].sort((a, b) => a.displayOrder - b.displayOrder),
    [entityTypes],
  );

  const activeCount = sortedTypes.filter((t) => t.isActive).length;

  const handleCreate = () => {
    setEditingType(null);
    setIsEditorOpen(true);
  };

  const handleEdit = (entityType: EntityType) => {
    setEditingType(entityType);
    setIsEditorOpen(true);
  };

  const handleSave = (data: CreateEntityTypePayload | UpdateEntityTypePayload) => {
    if (editingType) {
      updateMutation.mutate(
        { id: editingType.id, data: data as UpdateEntityTypePayload },
        {
          onSuccess: () => {
            setIsEditorOpen(false);
            setEditingType(null);
            toast({ variant: 'success', title: 'Entity type updated' });
          },
        },
      );
    } else {
      createMutation.mutate(data as CreateEntityTypePayload, {
        onSuccess: () => {
          setIsEditorOpen(false);
          setEditingType(null);
          toast({ variant: 'success', title: 'Entity type created' });
        },
      });
    }
  };

  const handleToggleActive = (entityType: EntityType) => {
    if (entityType.isActive) {
      deleteMutation.mutate(entityType.id, {
        onSuccess: () => toast({ variant: 'success', title: `${entityType.name} deactivated` }),
      });
    } else {
      updateMutation.mutate(
        { id: entityType.id, data: { is_active: true } },
        { onSuccess: () => toast({ variant: 'success', title: `${entityType.name} activated` }) },
      );
    }
  };

  if (isLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <div
            className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent"
            role="status"
            aria-label="Loading entity types"
          />
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Entity Types"
          description="Configure the entity types available for contacts (e.g., Individual, Corporation, Trust, Partnership)."
          action={
            <Button size="sm" onClick={handleCreate}>
              Add Entity Type
            </Button>
          }
        />
        <CardContent>
          {sortedTypes.length === 0 ? (
            <p className="py-4 text-sm text-gray-500 dark:text-gray-400">
              No entity types configured yet. Add your first entity type to get started.
            </p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" role="table">
                  <thead>
                    <tr className="border-b border-border text-left dark:border-border-dark">
                      <th className="pb-3 pr-4 font-medium text-gray-500 dark:text-gray-400">
                        Order
                      </th>
                      <th className="pb-3 pr-4 font-medium text-gray-500 dark:text-gray-400">
                        Name
                      </th>
                      <th className="pb-3 pr-4 font-medium text-gray-500 dark:text-gray-400">
                        Description
                      </th>
                      <th className="pb-3 pr-4 font-medium text-gray-500 dark:text-gray-400">
                        Status
                      </th>
                      <th className="pb-3 font-medium text-gray-500 dark:text-gray-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border dark:divide-border-dark">
                    {sortedTypes.map((entityType) => (
                      <tr key={entityType.id}>
                        <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">
                          {entityType.displayOrder}
                        </td>
                        <td className="py-3 pr-4 font-medium text-gray-900 dark:text-gray-100">
                          {entityType.name}
                        </td>
                        <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">
                          {entityType.description || '—'}
                        </td>
                        <td className="py-3 pr-4">
                          <Badge
                            variant={entityType.isActive ? 'success' : 'default'}
                            size="sm"
                            dot
                          >
                            {entityType.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className="py-3">
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(entityType)}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleActive(entityType)}
                            >
                              {entityType.isActive ? 'Deactivate' : 'Activate'}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                {activeCount} active entity type{activeCount !== 1 ? 's' : ''}
              </p>
            </>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      {isEditorOpen && (
        <EntityTypeEditorModal
          entityType={editingType}
          onSave={handleSave}
          onClose={() => {
            setIsEditorOpen(false);
            setEditingType(null);
          }}
          isSaving={createMutation.isPending || updateMutation.isPending}
        />
      )}
    </div>
  );
}

// ─── Entity Type Editor Modal ─────────────────────────────────────────────────

interface EntityTypeEditorModalProps {
  entityType: EntityType | null;
  onSave: (data: CreateEntityTypePayload | UpdateEntityTypePayload) => void;
  onClose: () => void;
  isSaving: boolean;
}

function EntityTypeEditorModal({
  entityType,
  onSave,
  onClose,
  isSaving,
}: EntityTypeEditorModalProps) {
  const [form, setForm] = useState({
    name: entityType?.name ?? '',
    description: entityType?.description ?? '',
    displayOrder: entityType?.displayOrder ?? 0,
    codePrefix: entityType?.codePrefix ?? '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (entityType) {
      onSave({
        name: form.name,
        description: form.description,
        display_order: form.displayOrder,
        code_prefix: form.codePrefix.trim() || undefined,
      } as UpdateEntityTypePayload);
    } else {
      onSave({
        name: form.name,
        description: form.description || undefined,
        display_order: form.displayOrder,
        code_prefix: form.codePrefix.trim() || undefined,
      } as CreateEntityTypePayload);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={entityType ? 'Edit Entity Type' : 'New Entity Type'}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Name"
          value={form.name}
          onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
          required
          maxLength={100}
          placeholder="e.g., Corporation, Trust, Partnership"
        />
        <Input
          label="Description"
          value={form.description}
          onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
          maxLength={250}
          placeholder="Optional description of this entity type"
        />
        <Input
          label="Client Code Prefix"
          value={form.codePrefix}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              codePrefix: e.target.value
                .replace(/[^A-Za-z0-9]/g, '')
                .toUpperCase()
                .slice(0, 10),
            }))
          }
          maxLength={10}
          placeholder="e.g., CORP"
          hint="Prefix for auto-generated client codes (e.g. CORP-00001). Leave blank to derive from the name."
        />
        <Input
          label="Display Order"
          type="number"
          value={String(form.displayOrder)}
          onChange={(e) => setForm((prev) => ({ ...prev, displayOrder: Number(e.target.value) }))}
          min={0}
          hint="Controls the order in which entity types appear in lists"
        />

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={isSaving} disabled={!form.name.trim()}>
            {entityType ? 'Update' : 'Create'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
